/** @jsxImportSource @emotion/react */
import React, { useCallback } from 'react'
import { TeamOutlined } from '@ant-design/icons'
import {
  HomeTeam,
  MyTeamOfficial,
  UpcomingGame,
  VisitingTeam,
} from '@sportninja/common/types/PlayerAttendance'
import { manageAttendanceButtonOutlinedStyle } from '../styles'
import { trackEvent, getPlatformType } from '../../../utils/analytics'
import { resolveVisitingTeamDisplay } from '@sportninja/common/features/game/placeholderVisitor/resolveVisitingTeamDisplay'
import { getOwnPlayerIds } from '@sportninja/common/features/game/attendance/utils/attendanceAccess'

export interface ManageTeamAttendanceProps {
  team: HomeTeam | VisitingTeam
  // Set when the viewer is a team official on this team → edit mode; null →
  // read-only (every viewer on the team sees the attendance).
  teamRep: MyTeamOfficial | null
  rowItem: UpcomingGame
  teamSide: 'home' | 'visitor'
  onPressManageAttendance: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean,
    isReadOnly: boolean,
    ownPlayerIds: string[]
  ) => void
}

const ManageTeamAttendance: React.FC<ManageTeamAttendanceProps> = ({
  team,
  teamRep,
  rowItem,
  teamSide,
  onPressManageAttendance,
}) => {
  const handlePress = useCallback(() => {
    trackEvent('home_game_card_manage_attendance_tapped', {
      platform: getPlatformType(),
      game_id: rowItem?.id,
      team_side: teamSide,
      is_read_only: !teamRep,
    })

    /*
     * Resolved for the visitor so a placeholder game passes the admin-typed
     * name, and `?? ''` because the consumer takes a definite string --
     * `visiting_team` is absent on those games (SN-8410).
     */
    const teamName =
      teamSide === 'home'
        ? rowItem?.home_team?.name ?? ''
        : resolveVisitingTeamDisplay(rowItem).name ?? ''

    onPressManageAttendance(
      rowItem?.id,
      teamRep?.game_player_roster_id ?? team.game_roster_id,
      team.id,
      teamName,
      rowItem?.show_waiver,
      !teamRep,
      // Read-only viewers may still set their own players' availability.
      getOwnPlayerIds(rowItem?.myPlayers, team.id)
    )
  }, [team, teamRep, rowItem, teamSide, onPressManageAttendance])

  return (
    <button css={manageAttendanceButtonOutlinedStyle} onClick={handlePress}>
      <span>GAME ATTENDANCE</span>
      <TeamOutlined style={{ fontSize: 16 }} />
    </button>
  )
}

export default React.memo(ManageTeamAttendance)
